// src/components/ExportModal.jsx
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { X, Copy, Check, Download, Loader2, AlertCircle, ChevronDown, ChevronUp } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import { generateCapturePrompt, generateExplorePrompt, generateCombinedExport, generateCompleteWorkflow, generateAIWorkflow, downloadMarkdownFile } from '../utils/exportToCursor';
import { useEntitlementSummary } from '../context/EntitlementSummaryContext';
import {
  evaluateEnhancementEntitlementHint,
  getEntitlementFooterHint,
} from '../utils/enhancementEntitlementHint';

function ExportModal({ isOpen, onClose, idea, tags }) {
  const {
    summary: entitlementSummary,
    loading: entitlementLoading,
    error: entitlementError,
  } = useEntitlementSummary();
  const enhancementEntitlement = evaluateEnhancementEntitlementHint({
    summary: entitlementSummary,
  });
  const canUseEnhancement = enhancementEntitlement.allowed;
  const aiWorkflowHint = getEntitlementFooterHint({
    loading: entitlementLoading,
    summary: entitlementSummary,
    allowed: canUseEnhancement,
    snapshotError: entitlementError,
  });
  // Template export states (collapsed in Advanced section)
  const [exportFormat, setExportFormat] = useState('combined');
  const [projectHint, setProjectHint] = useState('');
  const [exportText, setExportText] = useState('');
  const [copied, setCopied] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);
  
  // AI generation states (primary flow)
  const [isGenerating, setIsGenerating] = useState(false);
  const [generateError, setGenerateError] = useState(null);
  const [generatedMarkdown, setGeneratedMarkdown] = useState(null);
  const [downloadProgress, setDownloadProgress] = useState('');

  useEffect(() => {
    if (isOpen && idea) {
      // Reset all states when modal opens
      setIsGenerating(false);
      setGenerateError(null);
      setGeneratedMarkdown(null);
      setDownloadProgress('');
      setShowAdvanced(false);
      updateExportText();
    }
  }, [isOpen, idea, exportFormat, projectHint]);

  const updateExportText = () => {
    if (!idea) return;
    
    const safeTags = tags || {};
    
    let text = '';
    switch (exportFormat) {
      case 'capture':
        text = generateCapturePrompt(idea, safeTags, projectHint);
        break;
      case 'explore':
        text = generateExplorePrompt(idea, safeTags, projectHint);
        break;
      case 'combined':
        text = generateCombinedExport(idea, safeTags, projectHint);
        break;
      case 'complete':
        text = generateCompleteWorkflow(idea, safeTags, projectHint);
        break;
      default:
        text = '';
    }
    setExportText(text);
  };

  const handleCopy = async (textToCopy) => {
    try {
      await navigator.clipboard.writeText(textToCopy);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
      // Fallback for older browsers
      const textarea = document.createElement('textarea');
      textarea.value = textToCopy;
      textarea.style.position = 'fixed';
      textarea.style.opacity = '0';
      document.body.appendChild(textarea);
      textarea.select();
      try {
        document.execCommand('copy');
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      } catch (fallbackErr) {
        console.error('Fallback copy failed:', fallbackErr);
      }
      document.body.removeChild(textarea);
    }
  };

  const handleGenerate = async () => {
    if (!idea) {
      setGenerateError('No idea data available');
      return;
    }

    setIsGenerating(true);
    setGenerateError(null);
    setGeneratedMarkdown(null);
    setDownloadProgress('Generating AI-enhanced workflow...');

    try {
      // Generate AI workflow
      const markdown = await generateAIWorkflow(idea, tags);
      
      if (!markdown) {
        throw new Error('No content generated');
      }

      setGeneratedMarkdown(markdown);
      setDownloadProgress('');
      setIsGenerating(false);
      
    } catch (error) {
      console.error('Error generating AI workflow:', error);
      setGenerateError(error.message || 'Failed to generate workflow. Please try again.');
      setIsGenerating(false);
      setDownloadProgress('');
    }
  };

  const handleDownload = () => {
    if (!generatedMarkdown) return;
    
    const filename = (idea.idea || 'untitled-idea').toLowerCase().replace(/[^a-z0-9]+/g, '-');
    downloadMarkdownFile(generatedMarkdown, filename);
  };

  if (!isOpen || !idea) return null;

  return (
    <>
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50" 
        onClick={onClose}
      />
      
      {/* Modal */}
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col">
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
              Export to Cursor
            </h2>
            <button
              onClick={onClose}
              className="p-2 text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors"
              aria-label="Close modal"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-6 space-y-4">
            {/* Primary: AI Generation */}
            <div className="bg-gradient-to-r from-purple-50 to-blue-50 dark:from-purple-900/20 dark:to-blue-900/20 rounded-lg p-4 border border-purple-200 dark:border-purple-800">
              <div className="mb-4">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                  Generate AI-Enhanced Workflow
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Generate a complete, AI-enhanced markdown file with all four Cursor workflow steps (capture → explore → plan → execute) with real, actionable content.
                </p>
                {!canUseEnhancement && !entitlementLoading && aiWorkflowHint && (
                  <p className="text-sm text-amber-600 dark:text-amber-400 mt-2 font-medium">
                    {aiWorkflowHint}
                  </p>
                )}
                {entitlementLoading && (
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">Checking enhancement access…</p>
                )}
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                  <Link to="/ai-tools" className="text-primary hover:underline font-medium">
                    Read more about AI tools
                  </Link>
                </p>
              </div>
              
              {generateError && (
                <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md flex items-start gap-2">
                  <AlertCircle className="w-4 h-4 text-red-600 dark:text-red-400 mt-0.5 flex-shrink-0" />
                  <div className="flex-1">
                    <p className="text-sm text-red-800 dark:text-red-200 font-medium">Error</p>
                    <p className="text-xs text-red-700 dark:text-red-300 mt-1 whitespace-pre-line">{generateError}</p>
                    {generateError.includes('quota') && (
                      <a 
                        href="https://platform.openai.com/account/billing" 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-xs text-red-600 dark:text-red-400 hover:underline mt-2 inline-block"
                      >
                        Check OpenAI Billing →
                      </a>
                    )}
                  </div>
                  <button
                    onClick={() => setGenerateError(null)}
                    className="text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-200"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              )}
              
              {downloadProgress && (
                <div className="mb-4 p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-md">
                  <div className="flex items-center gap-2">
                    {isGenerating && <Loader2 className="w-4 h-4 text-blue-600 dark:text-blue-400 animate-spin" />}
                    <p className="text-sm text-blue-800 dark:text-blue-200">{downloadProgress}</p>
                  </div>
                </div>
              )}
              
              {!generatedMarkdown && !isGenerating && (
                <button
                  onClick={handleGenerate}
                  disabled={!idea || !canUseEnhancement || entitlementLoading}
                  className={`w-full flex items-center justify-center gap-2 px-4 py-3 rounded-md transition-all duration-200 font-medium ${
                    idea && canUseEnhancement && !entitlementLoading
                      ? 'bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white'
                      : 'bg-gray-400 text-white cursor-not-allowed opacity-50'
                  }`}
                  title={
                    !canUseEnhancement || entitlementLoading
                      ? aiWorkflowHint || "AI enhancement isn't available for your account right now. See Support."
                      : 'Generate AI-enhanced workflow'
                  }
                >
                  <Download className="w-5 h-5" />
                  Generate Workflow
                </button>
              )}

              {/* Generated Markdown Preview */}
              {generatedMarkdown && (
                <div className="mt-4 space-y-4">
                  <div className="bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700 p-6 max-h-[60vh] overflow-y-auto markdown-preview">
                    <ReactMarkdown
                      components={{
                        h1: ({...props}) => <h1 className="text-2xl font-bold mb-4 mt-6 text-gray-900 dark:text-white border-b border-gray-200 dark:border-gray-700 pb-2" {...props} />,
                        h2: ({...props}) => <h2 className="text-xl font-bold mb-3 mt-5 text-gray-900 dark:text-white" {...props} />,
                        h3: ({...props}) => <h3 className="text-lg font-semibold mb-2 mt-4 text-gray-900 dark:text-white" {...props} />,
                        h4: ({...props}) => <h4 className="text-base font-semibold mb-2 mt-3 text-gray-900 dark:text-white" {...props} />,
                        p: ({...props}) => <p className="mb-3 text-gray-700 dark:text-gray-300 leading-relaxed" {...props} />,
                        ul: ({...props}) => <ul className="list-disc list-inside mb-3 space-y-1 text-gray-700 dark:text-gray-300" {...props} />,
                        ol: ({...props}) => <ol className="list-decimal list-inside mb-3 space-y-1 text-gray-700 dark:text-gray-300" {...props} />,
                        li: ({...props}) => <li className="ml-4" {...props} />,
                        code: ({inline, ...props}) => 
                          inline ? (
                            <code className="bg-gray-100 dark:bg-gray-800 px-1.5 py-0.5 rounded text-sm font-mono text-gray-800 dark:text-gray-200" {...props} />
                          ) : (
                            <code className="block bg-gray-100 dark:bg-gray-800 p-3 rounded text-sm font-mono text-gray-800 dark:text-gray-200 overflow-x-auto mb-3" {...props} />
                          ),
                        pre: ({...props}) => <pre className="bg-gray-100 dark:bg-gray-800 p-3 rounded text-sm font-mono text-gray-800 dark:text-gray-200 overflow-x-auto mb-3" {...props} />,
                        blockquote: ({...props}) => <blockquote className="border-l-4 border-gray-300 dark:border-gray-600 pl-4 italic my-3 text-gray-600 dark:text-gray-400" {...props} />,
                        hr: ({...props}) => <hr className="my-6 border-gray-300 dark:border-gray-700" {...props} />,
                        strong: ({...props}) => <strong className="font-bold text-gray-900 dark:text-white" {...props} />,
                        em: ({...props}) => <em className="italic" {...props} />,
                      }}
                    >
                      {generatedMarkdown}
                    </ReactMarkdown>
                  </div>
                  
                  {/* Action Buttons */}
                  <div className="flex gap-3">
                    <button
                      onClick={handleDownload}
                      className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white rounded-md transition-all duration-200 font-medium"
                    >
                      <Download className="w-4 h-4" />
                      Download Markdown
                    </button>
                    <button
                      onClick={() => handleCopy(generatedMarkdown)}
                      className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-gray-600 hover:bg-gray-700 text-white rounded-md transition-all duration-200 font-medium"
                    >
                      {copied ? (
                        <>
                          <Check className="w-4 h-4" />
                          Copied!
                        </>
                      ) : (
                        <>
                          <Copy className="w-4 h-4" />
                          Copy to Clipboard
                        </>
                      )}
                    </button>
                    <button
                      onClick={handleGenerate}
                      className="px-4 py-2.5 bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 rounded-md transition-all duration-200 font-medium"
                    >
                      Regenerate
                    </button>
                  </div>
                </div>
              )}
              
              {isGenerating && (
                <div className="mt-4 text-center">
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    This may take 10-30 seconds
                  </p>
                </div>
              )}
            </div>

            {/* Advanced: Template Export (Collapsed) */}
            <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
              <button
                onClick={() => setShowAdvanced(!showAdvanced)}
                className="w-full flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-900 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
              >
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  Advanced: Template-Based Export
                </span>
                {showAdvanced ? (
                  <ChevronUp className="w-5 h-5 text-gray-500" />
                ) : (
                  <ChevronDown className="w-5 h-5 text-gray-500" />
                )}
              </button>
              
              {showAdvanced && (
                <div className="p-4 space-y-4 border-t border-gray-200 dark:border-gray-700">
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    Generate template-based export without AI. Faster but less detailed.
                  </p>
                  
                  {/* Export Format Selection */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Export Format
                    </label>
                    <select
                      value={exportFormat}
                      onChange={(e) => setExportFormat(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                    >
                      <option value="combined">Capture + Explore (Recommended)</option>
                      <option value="capture">Capture Issue Only</option>
                      <option value="explore">Explore Only</option>
                      <option value="complete">Complete Workflow (Capture + Explore + Plan + Execute)</option>
                    </select>
                  </div>

                  {/* Project Hint */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Target Project Hint (Optional)
                    </label>
                    <input
                      type="text"
                      value={projectHint}
                      onChange={(e) => setProjectHint(e.target.value)}
                      placeholder="e.g., epiphoric, my-app, etc."
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                    />
                    <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                      This is just for labeling in the export. It won't affect the generated prompt.
                    </p>
                  </div>

                  {/* Template Export Text */}
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                        Template Export Text
                      </label>
                      <button
                        onClick={() => handleCopy(exportText)}
                        className="flex items-center gap-2 px-3 py-1.5 text-sm bg-primary hover:bg-primary/90 text-white rounded-md transition-colors"
                      >
                        {copied ? (
                          <>
                            <Check className="w-4 h-4" />
                            Copied!
                          </>
                        ) : (
                          <>
                            <Copy className="w-4 h-4" />
                            Copy
                          </>
                        )}
                      </button>
                    </div>
                    <textarea
                      value={exportText}
                      readOnly
                      className="w-full h-48 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-white font-mono text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent resize-none"
                      onClick={(e) => e.target.select()}
                    />
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default ExportModal;
